/**
 * Presentation vocabulary for the avatar editor (APP_SPEC §13). The option
 * *ids* and the Zod schema are the shared source of truth in
 * `@perplexicon/validation` (and name the artwork directories); this file only
 * adds the web-side labels and colour swatches the pickers render with.
 * Turning an id into an artwork URL is `avatar-assets.ts`'s job, not this
 * file's.
 *
 * MODULARITY (APP_SPEC §13): every list below is valid with every `base`.
 * `getHairStyleOptions(base)` and friends take a `base` argument purely so
 * a future cosmetic could be *added* per base — they never *filter* by it,
 * and the tests assert that.
 */
import { AVATAR_BASES } from '@perplexicon/validation';
import type {
  AvatarBase,
  AvatarConfig,
  AvatarEyeColor,
  AvatarEyeShape,
  AvatarHairColor,
  AvatarHairStyle,
  AvatarSkinTone,
} from '@perplexicon/validation';

export interface AvatarOption<T extends string> {
  id: T;
  label: string;
}

export interface AvatarSwatch<T extends string> extends AvatarOption<T> {
  hex: string;
}

/**
 * The bodies, as the editor labels them: "Base 1" … "Base 10", numbered in
 * `AVATAR_BASES` order. The editor shows each as a portrait thumbnail, so the
 * label is the button's accessible name. The numbers are neutral on purpose
 * (APP_SPEC §13: a player never has to read a gendered word to pick a body).
 *
 * Saved avatars from before the bodies were numbered carry
 * `'feminine'`/`'androgynous'`/`'masculine'`; `avatarConfigSchema` maps those
 * onto `base-01`/`base-03`/`base-06`, so they never reach this list.
 */
export const BASE_OPTIONS: ReadonlyArray<AvatarOption<AvatarBase>> = AVATAR_BASES.map(
  (id, index) => ({ id, label: `Base ${String(index + 1)}` }),
);

export const SKIN_TONE_SWATCHES: ReadonlyArray<AvatarSwatch<AvatarSkinTone>> = [
  { id: 'porcelain', label: 'Porcelain', hex: '#f2d3c0' },
  { id: 'sand', label: 'Sand', hex: '#e6b48c' },
  { id: 'tan', label: 'Tan', hex: '#c98d63' },
  { id: 'umber', label: 'Umber', hex: '#8a5a3c' },
  { id: 'espresso', label: 'Espresso', hex: '#5a3826' },
];

/**
 * Not offered by the editor for now: only almond is drawn for every base
 * (`UNIVERSAL_EYE_SHAPE` in `avatar-assets.ts`), so the editor pins that one.
 * The list stays so a saved shape keeps its label and restoring the picker is
 * one `OptionRow` once every base has the other shapes.
 */
export const EYE_SHAPE_OPTIONS: ReadonlyArray<AvatarOption<AvatarEyeShape>> = [
  { id: 'round', label: 'Round' },
  { id: 'almond', label: 'Almond' },
  { id: 'hooded', label: 'Hooded' },
  { id: 'narrow', label: 'Narrow' },
];

/**
 * Not offered by the editor yet: the eyes are painted into each body layer and
 * there is no iris-overlay art to tint (APP_SPEC §13.1). The list stays so a
 * saved `eyeColor` keeps its label, and so restoring the picker is a
 * one-line change once the overlays exist.
 */
export const EYE_COLOR_SWATCHES: ReadonlyArray<AvatarSwatch<AvatarEyeColor>> = [
  { id: 'brown', label: 'Brown', hex: '#5a3a22' },
  { id: 'hazel', label: 'Hazel', hex: '#8a6b3a' },
  { id: 'blue', label: 'Blue', hex: '#3f6f9c' },
  { id: 'green', label: 'Green', hex: '#3f7d5a' },
];

export const HAIR_STYLE_OPTIONS: ReadonlyArray<AvatarOption<AvatarHairStyle>> = [
  { id: 'bald', label: 'Bald' },
  { id: 'buzzcut', label: 'Buzzcut' },
  { id: 'crop', label: 'Crop' },
  { id: 'fauxhawk', label: 'Fauxhawk' },
  { id: 'bob', label: 'Bob' },
  { id: 'wavy', label: 'Wavy' },
  { id: 'long-straight', label: 'Long' },
  { id: 'coils', label: 'Coils' },
  { id: 'high-bun', label: 'High bun' },
  { id: 'space-buns', label: 'Space buns' },
];

/** Swatch hexes are the ramp midtones in `scripts/avatar-variants.py`. */
export const HAIR_COLOR_SWATCHES: ReadonlyArray<AvatarSwatch<AvatarHairColor>> = [
  { id: 'black', label: 'Black', hex: '#1c1a19' },
  { id: 'dark-brown', label: 'Dark Brown', hex: '#38251b' },
  { id: 'brown', label: 'Brown', hex: '#5a3a22' },
  { id: 'light-brown', label: 'Light Brown', hex: '#8a6244' },
  { id: 'chestnut', label: 'Chestnut', hex: '#7a4632' },
  { id: 'auburn', label: 'Auburn', hex: '#7d3520' },
  { id: 'copper', label: 'Copper', hex: '#b85c32' },
  { id: 'red', label: 'Red', hex: '#9e3028' },
  { id: 'ginger', label: 'Ginger', hex: '#c46a3a' },
  { id: 'blond', label: 'Blond', hex: '#d9b168' },
  { id: 'golden-blond', label: 'Golden Blond', hex: '#e0bf72' },
  { id: 'strawberry-blond', label: 'Strawberry Blond', hex: '#d8a07a' },
  { id: 'platinum', label: 'Platinum', hex: '#e7dfc9' },
  { id: 'silver', label: 'Silver', hex: '#c9c6bf' },
  { id: 'white', label: 'White', hex: '#eeeae1' },
  { id: 'gray', label: 'Gray', hex: '#8f8c86' },
  { id: 'teal', label: 'Teal', hex: '#2f6f6a' },
  { id: 'blue', label: 'Blue', hex: '#3f5f9c' },
  { id: 'purple', label: 'Purple', hex: '#76558e' },
  { id: 'pink', label: 'Pink', hex: '#d67aaa' },
];

/** All option lists, keyed by the `AvatarConfig` field they populate. */
export const AVATAR_FIELD_OPTIONS = {
  base: BASE_OPTIONS,
  skinTone: SKIN_TONE_SWATCHES,
  eyeShape: EYE_SHAPE_OPTIONS,
  eyeColor: EYE_COLOR_SWATCHES,
  hairStyle: HAIR_STYLE_OPTIONS,
  hairColor: HAIR_COLOR_SWATCHES,
} as const;

// The `base`-parameterized getters below deliberately ignore `base`: no
// cosmetic option is gender-locked (APP_SPEC §13).
export const getBaseOptions = () => BASE_OPTIONS;
export const getSkinToneOptions = (_base?: AvatarBase) => SKIN_TONE_SWATCHES;
export const getEyeShapeOptions = (_base?: AvatarBase) => EYE_SHAPE_OPTIONS;
export const getEyeColorOptions = (_base?: AvatarBase) => EYE_COLOR_SWATCHES;
export const getHairStyleOptions = (_base?: AvatarBase) => HAIR_STYLE_OPTIONS;
export const getHairColorOptions = (_base?: AvatarBase) => HAIR_COLOR_SWATCHES;

export { AVATAR_BASES };
export type { AvatarConfig };
